import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { Users, GraduationCap, Calendar, DollarSign } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { ZeroBalancePanel } from '../components/ZeroBalancePanel'
import { hasAdminRights } from '../lib/roles'

interface DashboardStats {
  totalStudents: number
  totalTutors: number
  totalSessions: number
  totalRevenue: number
}

interface ActivityItem {
  id: string
  type: 'session' | 'student' | 'tutor' | 'topup'
  message: string
  timestamp: string
}

export function Dashboard() {
  const { profile } = useAuth()
  const isAdmin = hasAdminRights(profile?.role)

  const [stats, setStats] = useState<DashboardStats>({
    totalStudents: 0,
    totalTutors: 0,
    totalSessions: 0,
    totalRevenue: 0
  })
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!profile) return
    fetchStats()
    fetchRecentActivity()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile])

  const fetchStats = async () => {
    try {
      const [studentsRes, tutorsRes, sessionsRes] = await Promise.all([
        supabase.from('students').select('id', { count: 'exact' }),
        supabase.from('tutors').select('id', { count: 'exact' }),
        supabase.from('sessions').select('id', { count: 'exact' })
      ])

      // Revenue is money — admins only.
      let totalRevenue = 0
      if (isAdmin) {
        const topupsRes = await supabase.from('topups').select('amount_usd')
        totalRevenue = topupsRes.data?.reduce((sum, topup) => sum + (topup.amount_usd || 0), 0) || 0
      }

      setStats({
        totalStudents: studentsRes.count || 0,
        totalTutors: tutorsRes.count || 0,
        totalSessions: sessionsRes.count || 0,
        totalRevenue
      })
    } catch (error) {
      console.error('Error fetching stats:', error)
    } finally {
      setLoading(false)
    }
  }

  const statCards = [
    {
      title: 'Total Students',
      value: stats.totalStudents,
      icon: Users,
      color: 'bg-blue-500',
      lightColor: 'bg-blue-100 text-blue-800'
    },
    {
      title: 'Total Tutors',
      value: stats.totalTutors,
      icon: GraduationCap,
      color: 'bg-green-500',
      lightColor: 'bg-green-100 text-green-800'
    },
    {
      title: 'Total Sessions',
      value: stats.totalSessions,
      icon: Calendar,
      color: 'bg-purple-500',
      lightColor: 'bg-purple-100 text-purple-800'
    },
    // Total Revenue is money — only shown to admins.
    ...(isAdmin ? [{
      title: 'Total Revenue',
      value: `$${stats.totalRevenue.toFixed(2)}`,
      icon: DollarSign,
      color: 'bg-yellow-500',
      lightColor: 'bg-yellow-100 text-yellow-800'
    }] : [])
  ]

  const fetchRecentActivity = async () => {
    try {
      const activities: ActivityItem[] = []

      // Fetch recent sessions
      const { data: sessions } = await supabase
        .from('sessions')
        .select('*, students(name), tutors(name)')
        .order('created_at', { ascending: false })
        .limit(3)

      if (sessions) {
        sessions.forEach((session: any) => {
          activities.push({
            id: `session-${session.id}`,
            type: 'session',
            message: `New session logged between ${session.students?.name || 'Unknown Student'} and ${session.tutors?.name || 'Unknown Tutor'}`,
            timestamp: session.created_at
          })
        })
      }

      // Fetch recent students
      const { data: students } = await supabase
        .from('students')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(2)

      if (students) {
        students.forEach((student: any) => {
          activities.push({
            id: `student-${student.id}`,
            type: 'student',
            message: `New student registered: ${student.name}`,
            timestamp: student.created_at
          })
        })
      }

      // Fetch recent top-ups — money, admins only.
      if (isAdmin) {
        const { data: topups } = await supabase
          .from('topups')
          .select('*, students(name)')
          .order('created_at', { ascending: false })
          .limit(2)

        if (topups) {
          topups.forEach((topup: any) => {
            activities.push({
              id: `topup-${topup.id}`,
              type: 'topup',
              message: `${topup.students?.name || 'Student'} topped up ${topup.minutes_added} minutes ($${topup.amount_usd})`,
              timestamp: topup.created_at
            })
          })
        }
      }

      // Sort all activities by timestamp
      activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())

      // Take only the 5 most recent
      setRecentActivity(activities.slice(0, 5))
    } catch (error) {
      console.error('Error fetching recent activity:', error)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-400"></div>
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Dashboard Overview</h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {statCards.map((card) => {
          const Icon = card.icon
          return (
            <div key={card.title} className="bg-white dark:bg-surface-100 rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg ${card.color}`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <span className={`text-sm font-medium px-2 py-1 rounded ${card.lightColor} dark:bg-opacity-20`}>
                  +12%
                </span>
              </div>
              <h3 className="text-gray-500 dark:text-gray-400 text-sm font-medium">{card.title}</h3>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{card.value}</p>
            </div>
          )
        })}
      </div>

      {isAdmin && <ZeroBalancePanel />}

      {/* Recent Activity */}
      <div className="bg-white dark:bg-surface-100 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h2>
        </div>
        <div className="p-6">
          {recentActivity.length > 0 ? (
            <div className="space-y-4">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-center space-x-4 py-2">
                  <div className={`flex-shrink-0 w-2 h-2 rounded-full ${
                    activity.type === 'session' ? 'bg-green-500' :
                    activity.type === 'student' ? 'bg-blue-500' :
                    activity.type === 'topup' ? 'bg-yellow-500' :
                    'bg-purple-500'
                  }`}></div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900 dark:text-white">
                      {activity.message}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
              No recent activity to display
            </p>
          )}
        </div>
      </div>
    </div>
  )
}